import AsyncStorage from "@react-native-async-storage/async-storage";
import dayjs from "dayjs";
import { Link, router } from "expo-router";
import { useField, useForm } from "mobx-easy-form";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef } from "react";
import { Image, Keyboard, Platform, StyleSheet } from "react-native";
import * as yup from "yup";
import { Button } from "~/components/Button";
import { Collapsible } from "~/components/Collapsible";
import { IconButton } from "~/components/IconButton";
import { Pressable } from "~/components/Pressable";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { TextInput } from "~/components/TextInput";
import { View } from "~/components/View";
import { constants } from "~/constants";
import { useAlert } from "~/hooks/useAlert";
import { state } from "~/store";
import { styleConstants } from "~/style/styleConstants";
import { promptYesNo } from "~/utils/promptYesNo";
import { GenderFemale, GenderMale, GenderOther } from "../gender-icons";
import { FormattedMessage, getLocaleURL } from "../intl";
import { LanguagePicker } from "../intl/LanguagePicker";
import { Logo } from "../logo/Logo";
import { useResponsive } from "./useResponsive";
import type { TextInput as RNTextInput } from "react-native";

export const LiveVideoDataFormScreen = observer(
  function LiveVideoDataFormScreen() {
    const r = useResponsive();
    const alert = useAlert();

    useEffect(function redirectToLoginOnApple() {
      if (Platform.OS !== "ios") return;

      async function runRedirectToLoginOnApple() {
        const isSignedIn = await AsyncStorage.getItem(
          constants.ASYNC_STORAGE_KEYS.IS_LOGGED_IN_IOS,
        );

        if (isSignedIn !== "true") {
          router.replace("/login");
        }
      }
      runRedirectToLoginOnApple();
    }, []);

    const form = useForm({
      async onSubmit(formData) {
        Keyboard.dismiss();
        // We persist the raw string values to storage, so we can use them in the future
        await AsyncStorage.setItem(
          constants.ASYNC_STORAGE_KEYS.NAME,
          formData.rawValues.name,
        );
        await AsyncStorage.setItem(
          constants.ASYNC_STORAGE_KEYS.AGE,
          formData.rawValues.age,
        );
        await AsyncStorage.setItem(
          constants.ASYNC_STORAGE_KEYS.SEX,
          formData.rawValues.sex,
        );

        state.localUserDetails.name = formData.values.name;
        state.localUserDetails.age = formData.values.age;
        state.localUserDetails.sex = formData.values.sex;

        const message = (
          <Text alignLeft>
            {intl.formatMessage(
              {
                defaultMessage:
                  "By using this video chat, you agree to our <link>guidelines</link>! Here are some rules to ensure a great experience:{br}{br}<bold>Be Respectful:</bold> Treat others with kindness and courtesy.{br}<bold>Keep Your Face Visible:</bold> Ensure your face is visible on camera.{br}<bold>No Harassment:</bold> Any form of harassment or disrespectful behavior will not be tolerated.{br}<bold>Follow Community Guidelines:</bold> Familiarize yourself with our <link>community guidelines</link> and abide by them.{br}{br}Let's create a friendly and safe space for everyone to enjoy!",
                id: "3WcUzW",
                description:
                  "Message for the alert that asks user to start a live video chat",
              },
              {
                br: "\n",
                bold: (chunks) => <Text weightExtraBold>{chunks}</Text>,
                link: (chunks) => (
                  <Link href="https://bokbok.io/guidelines" target="_blank">
                    <Text colorTheme weightExtraBold>
                      {chunks}
                    </Text>
                  </Link>
                ),
              },
            )}
          </Text>
        );

        const shouldContinue = await promptYesNo(
          {
            title: intl.formatMessage({
              defaultMessage: "Rules",
              id: "80eH0x",
              description:
                "Title for the alert that asks user to read the rules before starting a live video chat",
            }),
            // @ts-expect-error promptYesNo should be able to handle <Text> elements, and not just strings, but it doesn't currently
            message: message,
            yesText: intl.formatMessage({
              defaultMessage: "Accept",
              id: "D7ViN1",
              description: "Text for accept button in overlay",
            }),
            noText: intl.formatMessage({
              defaultMessage: "Cancel",
              id: "7tsY9l",
              description: "Cancel button in alert",
            }),
          },
          alert,
        );

        if (!shouldContinue) return;

        router.navigate(getLocaleURL("/video-settings"));
      },
    });

    const name = useField<string, string>({
      form,
      id: "name",
      initialValue: "",
      validationSchema: yup.string().required().trim().min(1).max(64),
    });

    const age = useField<string, number>({
      form,
      id: "age",
      initialValue: "",
      validate(value) {
        if (value === "") {
          return { error: "Date of birth is required" };
        }
        const date = dayjs(value, "DD/MM/YYYY", true);
        if (date.isValid() === false) {
          return { error: "Date should be in format DD/MM/YYYY" };
        }

        const age = dayjs().diff(date, "years");

        if (age < 18) {
          return { error: "You must be 18 or older" };
        }

        if (age > 100) {
          return { error: "You must be less than 100 years old" };
        }

        return { parsed: age };
      },
    });

    const sex = useField<
      undefined | "male" | "female" | "other",
      "male" | "female" | "other"
    >({
      form,
      id: "sex",
      initialValue: undefined,
      validationSchema: yup
        .string()
        .oneOf(["male", "female", "other"])
        .required(),
    });

    const isMaleGenderSelected = sex.state.value === "male";
    const isFemaleGenderSelected = sex.state.value === "female";
    const isOtherGenderSelected = sex.state.value === "other";

    useEffect(
      function populateUserDetailsFromPersistanceEffect() {
        async function runPopulateUserDetailsFromPersistanceEffect() {
          const schema = yup
            .object({
              name: yup.string().nullable(),
              age: yup
                .string()
                .matches(/^\d\d\/\d\d\/\d\d\d\d$/)
                .nullable(),
              sex: yup
                .string()
                .oneOf(["male", "female", "other"] as const)
                .nullable(),
            })
            .required();

          const rawUserDetails = {
            name: await AsyncStorage.getItem(constants.ASYNC_STORAGE_KEYS.NAME),
            age: await AsyncStorage.getItem(constants.ASYNC_STORAGE_KEYS.AGE),
            sex: await AsyncStorage.getItem(constants.ASYNC_STORAGE_KEYS.SEX),
          };

          try {
            const userDetails = schema.validateSync(rawUserDetails);
            if (userDetails.name) {
              name.actions.onChange(userDetails.name);
            }
            if (userDetails.age) {
              age.actions.onChange(userDetails.age);
            }
            if (userDetails.sex) {
              sex.actions.onChange(userDetails.sex);
            }
          } catch (error) {
            console.log(
              "Invalid user details in persisted storage.",
              JSON.stringify(rawUserDetails),
              error,
            );
          }
        }

        runPopulateUserDetailsFromPersistanceEffect();
      },
      [age, name, sex],
    );

    const dateOfBirthRef = useRef<RNTextInput>(null);
    return (
      <Screen withBottomInset withTopInset colorBackgroundLightDark>
        <View flex alignItemsCenter colorBackgroundLight>
          <Image
            source={require("./tile.png")}
            resizeMode="repeat"
            width={100}
            height={100}
            style={{
              ...StyleSheet.absoluteFillObject,
              width: "100%",
              height: "100%",
            }}
          />

          <View
            style={{
              width: r("100%", 640, 1024),
              minWidth: 400,
              height: "100%",
              backgroundColor: styleConstants.colorBackgroundLightDark,
            }}
          >
            <View
              flexDirectionRow
              alignItemsCenter
              alignSelfCenter
              style={{
                minHeight: 10,
                height: r(80, 100, 140),
                maxHeight: 250,
              }}
            >
              <Logo
                size={r(64, 64, 116)}
                backgroundColor={styleConstants.colorBackgroundTheme}
                strokeColor={styleConstants.colorBackgroundWhite}
              />

              <View style={{ width: r(20, 20, 40) }} />

              <Text
                weightExtraBold
                colorTheme
                style={{
                  fontSize: r(50, 50, 80),
                  lineHeight: undefined,
                }}
                alignCenter
              >
                {intl.formatMessage({
                  defaultMessage: "BokBok Live",
                  id: "qMC/dU",
                  description:
                    "Header - BokBok Live - Do not translate unless really necessary",
                })}
              </Text>
            </View>

            <View
              colorBackgroundDarkLighter
              style={{
                paddingHorizontal: r(16, 64, 128),
                paddingVertical: r(16, 32, 48),
              }}
            >
              <View
                style={{
                  width: r("100%", 480, 480),
                  alignSelf: r("auto", "auto", "center"),
                }}
              >
                <Text colorLight sizeExtraLarge weightExtraBold alignCenter>
                  <FormattedMessage
                    id="EtXb2J"
                    defaultMessage="Meet & Live Video Chat"
                    description="Title on HomeScreen"
                  />
                </Text>

                {/* }Meet New People & Friends on BokBok */}
                <Text colorLight sizeExtraLarge weightExtraBold alignCenter />

                <Spacer medium />

                <Text colorLight sizeMedium>
                  {intl.formatMessage({
                    defaultMessage:
                      "No registration required for BokBok's video chat - just enter your name, birth date and gender to instantly start live video chatting.",
                    id: "J7q6Ww",
                    description: "Description on main webpage",
                  })}
                </Text>
              </View>
            </View>
            <View>
              <View
                style={{
                  width: r("100%", 480, 480),
                  alignSelf: "center",
                  padding: r(
                    styleConstants.spacingLarge,
                    styleConstants.spacingLarge,
                    0,
                  ),
                }}
              >
                <Spacer extraLarge />

                <TextInput
                  editable={Platform.OS !== "ios"}
                  label={intl.formatMessage({
                    defaultMessage: "Name",
                    id: "HOvzK4",
                    description: "Text input label",
                  })}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Your name",
                    id: "PDbi5a",
                    description: "Placeholder on TextInput for entering name",
                  })}
                  value={name.state.value}
                  maxLength={64}
                  onChangeText={name.actions.onChange}
                  onBlur={() => {
                    name.actions.onChange(name.state.value.trim());
                    name.actions.onBlur();
                  }}
                  caption={name.computed.ifWasEverBlurredThenError}
                  error={!!name.computed.ifWasEverBlurredThenError}
                  blurOnSubmit={false}
                  autoCapitalize="words"
                  autoCorrect={false}
                  autoComplete="name"
                  enterKeyHint="next"
                  spellCheck={false}
                  onSubmitEditing={() => {
                    dateOfBirthRef.current?.focus();
                  }}
                />

                <Spacer extraLarge />

                <TextInput
                  ref={dateOfBirthRef}
                  label={intl.formatMessage({
                    defaultMessage: "Date of birth (DD/MM/YYYY)",
                    id: "VVkddC",
                    description: "Text input label",
                  })}
                  placeholder={intl.formatMessage({
                    id: "70+KGL",
                    defaultMessage: "Date of birth",
                    description: "Placeholder on date of birth input",
                  })}
                  maxLength={10}
                  keyboardType="numeric"
                  blurOnSubmit
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="off"
                  enterKeyHint="next"
                  spellCheck={false}
                  value={age.state.value}
                  onBlur={age.actions.onBlur}
                  onFocus={age.actions.onFocus}
                  caption={
                    age.state.wasEverBlurred || age.state.value !== ""
                      ? age.computed.error
                      : undefined
                  }
                  error={
                    age.state.wasEverBlurred || age.state.value !== ""
                      ? !!age.computed.error
                      : false
                  }
                  onChangeText={(value) => {
                    if (value.length > 10) {
                      return;
                    }

                    if (
                      age.state.value.length < value.length &&
                      /^\d\d$/.test(value)
                    ) {
                      age.actions.onChange(value + "/");
                      return;
                    }

                    // When user types in a month without the leading 0 we prefill it for them
                    if (
                      age.state.value.length < value.length &&
                      /^\d\d\/\d$/.test(value) &&
                      Number(value[3]) > 1
                    ) {
                      age.actions.onChange(
                        value.slice(0, 3) + "0" + value[3] + "/",
                      );
                      return;
                    }

                    // In the case when they type the slash "DD/1/"
                    if (
                      age.state.value.length < value.length &&
                      /^\d\d\/\d\/$/.test(value)
                    ) {
                      age.actions.onChange(
                        value.slice(0, 2) + "/0" + value[3] + "/",
                      );
                      return;
                    }

                    // In the case when they type the slash "DD/MM/9"
                    if (
                      age.state.value.length < value.length &&
                      /^\d\d\/\d\d\/\d$/.test(value) &&
                      Number(value[6]) > 3
                    ) {
                      age.actions.onChange(value.slice(0, 6) + "19" + value[6]);
                      return;
                    }

                    if (
                      age.state.value.length < value.length &&
                      /^\d\d\/\d\d$/.test(value)
                    ) {
                      age.actions.onChange(value + "/");
                      return;
                    }

                    age.actions.onChange(value);
                  }}
                />

                <Spacer extraLarge />

                <Text weightSemiBold>
                  {intl.formatMessage({
                    id: "u1GShg",
                    defaultMessage: "Your gender",
                    description: "Text input label",
                  })}
                </Text>

                <Spacer medium />

                {/* Gender start */}
                <View
                  borderRadiusSmall
                  flexDirectionRow
                  style={[S.genderContainer, { gap: 4 }]}
                >
                  <Pressable
                    style={S.button}
                    flex
                    centerContent
                    colorBackgroundTheme={isFemaleGenderSelected}
                    colorBackgroundWhite={!isFemaleGenderSelected}
                    onPress={() => {
                      Keyboard.dismiss();
                      sex.actions.onChange("female");
                    }}
                  >
                    <GenderFemale />

                    <Text colorLight={isFemaleGenderSelected}>
                      <FormattedMessage
                        id="qEsNNX"
                        defaultMessage="Female"
                        description="Option in CreateProfileGenderScreen if you are a Female you can select this option"
                      />
                    </Text>
                  </Pressable>

                  <Pressable
                    style={S.button}
                    flex
                    centerContent
                    colorBackgroundTheme={isMaleGenderSelected}
                    colorBackgroundWhite={!isMaleGenderSelected}
                    onPress={() => {
                      Keyboard.dismiss();
                      sex.actions.onChange("male");
                    }}
                  >
                    <GenderMale />

                    <Text colorLight={isMaleGenderSelected}>
                      <FormattedMessage
                        id="Ve64ij"
                        defaultMessage="Male"
                        description="Option in CreateProfileGenderScreen. If your gender is male you can select this option"
                      />
                    </Text>
                  </Pressable>

                  <Pressable
                    style={S.button}
                    flex
                    centerContent
                    colorBackgroundTheme={isOtherGenderSelected}
                    colorBackgroundWhite={!isOtherGenderSelected}
                    onPress={() => {
                      Keyboard.dismiss();
                      sex.actions.onChange("other");
                    }}
                  >
                    <GenderOther />

                    <Text colorLight={isOtherGenderSelected}>
                      <FormattedMessage
                        id="36/11/"
                        defaultMessage="Other"
                        description="Option in CreateProfileGenderScreen. If your gender is not male or female you can select this option."
                      />
                    </Text>
                  </Pressable>
                </View>
                {/* Gender end */}
                <Collapsible
                  collapsed={!sex.computed.ifWasEverFocusedThenError}
                >
                  <Spacer small />
                  <Text
                    sizeMedium
                    colorDanger={!!sex.computed.ifWasEverFocusedThenError}
                    weightRegular
                  >
                    {sex.computed.ifWasEverFocusedThenError}
                  </Text>
                </Collapsible>

                <View style={{ height: r(32, 48, 48) }} />

                <Button
                  colorTheme
                  style={{ height: r(48, 62, 68) }}
                  onPress={form.actions.submit}
                >
                  <Text sizeExtraLarge weightExtraBold colorLight>
                    <FormattedMessage
                      id="yo1vLg"
                      defaultMessage="Let's go!"
                      description="Let's go! - text on the live video data form screen"
                    />
                  </Text>
                </Button>
              </View>
            </View>

            <View style={{ height: r(32, 48, 48) }} />

            <View style={{ paddingHorizontal: r(16, 16, 32) }}>
              <Text sizeMedium weightSemiBold>
                {intl.formatMessage(
                  {
                    defaultMessage:
                      "Discover diverse cultures, fascinating conversations and friendly faces with the tap of a button.{newline}{newline}Looking to make new connections? BokBok lets you live video chat with people from around the world. An exciting way to meet people, make friends or even try dating!{newline}{newline}",
                    id: "0E64SJ",
                    description: "Description on main webpage",
                  },
                  {
                    newline: "\n",
                  },
                )}
                {/* Download BokBok now on iOS or Android to access an endless
                stream of chats. Meet friends from across the globe or
                connect with someone locally - you'll never know who you'll
                encounter next! */}
              </Text>

              <Spacer extraLarge />
              <View alignSelfStretch centerContent flexDirectionRow>
                <LanguagePicker />

                {Platform.OS === "ios" && (
                  <>
                    <Spacer />
                    <IconButton
                      iconSize={24}
                      iconColor={styleConstants.colorTextDark}
                      iconName="account-cog-outline"
                      onPress={() => {
                        router.navigate(getLocaleURL("/my-account"));
                      }}
                    />
                  </>
                )}
              </View>
            </View>
          </View>
        </View>
      </Screen>
    );
  },
);

const S = StyleSheet.create({
  genderContainer: {
    borderColor: styleConstants.colorTextDarkSoft,
  },
  button: {
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: styleConstants.colorBackgroundGray,
    borderRadius: 4,
  },
  checkbox: { height: 62 },
});
